/**
 * FactoryWsProvider — single WebSocket connection for go-code-factory real-time events.
 *
 * Mounted inside AuthGuard (authenticated), outside the router, so the connection
 * survives page navigation. On token rotation, the connection is torn down and rebuilt.
 */
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  createFactoryWsConnection,
  dispatchFactoryWsEvent,
  type FactoryWsConnection,
  FactoryWsContext,
} from '../lib/factoryWs';
import { useAuthStore } from '../stores/authStore';
import { useFactoryWsStore } from '../stores/factoryWsStore';

interface Props {
  children: ReactNode;
}

export function FactoryWsProvider({ children }: Props) {
  const token = useAuthStore((state) => state.accessToken);
  const queryClient = useQueryClient();
  const connRef = useRef<FactoryWsConnection | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected');
  const setStoreStatus = useFactoryWsStore((s) => s.setConnectionStatus);

  useEffect(() => {
    if (!token) return;

    // Tear down any existing connection before rebuilding with new token.
    connRef.current?.destroy();
    connRef.current = null;

    connRef.current = createFactoryWsConnection(
      async () => token,
      (status) => {
        setConnectionStatus(status);
        setStoreStatus(status);
      },
      dispatchFactoryWsEvent,
      queryClient,
    );

    return () => {
      connRef.current?.destroy();
      connRef.current = null;
      setConnectionStatus('disconnected');
      setStoreStatus('disconnected');
    };
  }, [token, queryClient, setStoreStatus]);

  const value = useMemo(
    () => ({
      subscribe: (runIds: string[]) => connRef.current?.subscribe(runIds),
      unsubscribe: (runIds: string[]) => connRef.current?.unsubscribe(runIds),
      connectionStatus,
    }),
    [connectionStatus],
  );

  return <FactoryWsContext.Provider value={value}>{children}</FactoryWsContext.Provider>;
}
